import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import PageLoading from "@/app/components/shared/pageLoading";
import { useSelector } from "react-redux";
import { RootState } from "@/app/store/store";
import dataService from "@/app/services/dataService";
import {
  ApproveUserRequest,
  CustomerUserDto,
  ManageUsersResponse,
  SecondaryUserRequest,
} from "@/app/models/accountProfile/types";
import AppDialog from "@/app/utils/appDialog";
import { SecondaryUserDialog } from "./secondaryUserDialog";
import { Lookup } from "@/app/models/common/types";
import { OptionGroup } from "@/app/models/checkout/types";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { ShipToSettingModel } from "@/app/models/orderNotification/types";

export const ManageUsers: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const userId = useSelector(
    (state: RootState) => state.userState.user.id ?? 0
  );
  const customerNumber = useSelector(
    (state: RootState) => state.userState.user?.customerNumber ?? null
  );
  const [pageLoading, setPageLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [model, setModel] = useState<ManageUsersResponse>();
  const pendingUsers: CustomerUserDto[] = model?.pendingSecondaryUsers ?? [];

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<CustomerUserDto | null>(
    null
  );

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [userToEdit, setUserToEdit] = useState<CustomerUserDto | null>(null);
  const [customerShipTos, setCustomerShipTos] = useState<OptionGroup[]>([]);
  const [shipTosLoading, setShipTosLoading] = useState(false);

  const [validationErrors, setValidationErrors] = useState<{
    firstName?: string;
    lastName?: string;
    email?: string;
  }>({});

  const updateUserField = (field: keyof CustomerUserDto, value: string) => {
    setUserToEdit((old) => {
      if (!old) return old;
      const updated = { ...old, [field]: value };
      validateUser(updated);
      return updated;
    });
  };

  const [sortField, setSortField] =
    useState<keyof CustomerUserDto>("firstName");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const handleSort = (field: keyof CustomerUserDto) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const sortedSecondaryUsers = React.useMemo(() => {
    if (!model?.secondaryUsers) return [];

    const sorted = [...model.secondaryUsers].sort((a, b) => {
      let valA = a[sortField];
      let valB = b[sortField];

      if (sortField === "lastLoginDate") {
        const dateA = new Date((valA ?? "") as string).getTime();
        const dateB = new Date((valB ?? "") as string).getTime();

        if (sortDirection === "asc") {
          return dateA - dateB;
        } else {
          return dateB - dateA;
        }
      }

      if (valA == null) valA = "";
      if (valB == null) valB = "";

      if (typeof valA === "string" && typeof valB === "string") {
        return sortDirection === "asc"
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA);
      }

      const numA = Number(valA);
      const numB = Number(valB);

      if (sortDirection === "asc") {
        return numA > numB ? 1 : -1;
      } else {
        return numA < numB ? 1 : -1;
      }
    });

    return sorted;
  }, [model?.secondaryUsers, sortField, sortDirection]);

  const isOrderNotificationSelected =
    !!userToEdit?.availableCustomerRoles?.some((r: any) => {
      const text = (r.text ?? "").toString().toLowerCase();
      const value = (r.value ?? "").toString().toLowerCase();
      const candidate = `${text} ${value}`;
      return candidate.includes("order") && candidate.includes("notification");
    });

  const [pendingUserRoles, setPendingUserRoles] = useState<
    Record<number, number[]>
  >({});

  const [pendingUserToApprove, setPendingUserToApprove] =
    useState<CustomerUserDto | null>(null);
  const [noRoleDialogOpen, setNoRoleDialogOpen] = useState(false);
  const [approveSuccessDialogOpen, setApproveSuccessDialogOpen] =
    useState(false);

  const [denySuccessDialogOpen, setDenySuccessDialogOpen] = useState(false);
  const [deniedUser, setDeniedUser] = useState<CustomerUserDto | null>(null);

  const [editSuccessDialogOpen, setEditSuccessDialogOpen] = useState(false);
  const [createSuccessDialogOpen, setCreateSuccessDialogOpen] = useState(false);

  const [editedUser, setEditedUser] = useState<CustomerUserDto | null>(null);
  const togglePendingRole = (userId: number, roleId: number) => {
    setPendingUserRoles((prev) => {
      const existing = prev[userId] ?? [];
      const alreadySelected = existing.includes(roleId);
      const updated = alreadySelected
        ? existing.filter((id) => id !== roleId)
        : [...existing, roleId];
      return { ...prev, [userId]: updated };
    });
  };

  const validateUser = (user: CustomerUserDto | null) => {
    const errors: { firstName?: string; lastName?: string; email?: string } =
      {};

    if (!user?.firstName?.trim()) {
      errors.firstName = "The First Name field is required.";
    }

    if (!user?.lastName?.trim()) {
      errors.lastName = "The Last Name field is required.";
    }

    if (!user?.email?.trim()) {
      errors.email = "The Email field is required.";
    }

    setValidationErrors(errors);

    return Object.keys(errors).length === 0;
  };

  const handleApprovePendingUser = async (user: CustomerUserDto) => {
    const selectedRoleIds = pendingUserRoles[user.id] ?? [];

    if (selectedRoleIds.length === 0) {
      setPendingUserToApprove(user);
      setNoRoleDialogOpen(true);
      return;
    }

    const payload: ApproveUserRequest = {
      approve: true,
      customerRoleIds: selectedRoleIds,
    };

    try {
      setLoading(true);
      setError(null);

      const res = await dataService.approvePendingUser(payload, user.id);

      if (res?.success) {
        setApproveSuccessDialogOpen(true);
        await loadManageUsers();
      } else {
        setError(res?.error || "Failed to approve user.");
      }
    } catch (e: any) {
      setError(
        e?.response?.data?.message || e?.message || "Failed to approve user."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDenyPendingUser = async (user: CustomerUserDto) => {
    const payload: ApproveUserRequest = {
      approve: false,
      customerRoleIds: [],
    };

    try {
      setLoading(true);
      setError(null);

      const res = await dataService.approvePendingUser(payload, user.id);

      if (res?.success) {
        setDeniedUser(user); // needed for "undo"
        setDenySuccessDialogOpen(true);
        await loadManageUsers();
      } else {
        setError(res?.error || "Failed to deny user.");
      }
    } catch (e: any) {
      setError(
        e?.response?.data?.message || e?.message || "Failed to deny user."
      );
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (user: CustomerUserDto) => {
    setValidationErrors({});
    setUserToEdit(user);
    setEditDialogOpen(true);
  };

  const closeEditDialog = () => {
    setEditDialogOpen(false);
    setValidationErrors({});
  };

  const closeAddDialog = () => {
    setAddDialogOpen(false);
    setValidationErrors({});
  };

  const handleOpenDeleteDialog = (user: CustomerUserDto) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  const handleSaveEditedUser = async (shipTos: ShipToSettingModel[]) => {
    if (!userToEdit) return;

    if (!validateUser(userToEdit)) return;

    const payload: SecondaryUserRequest = {
      FirstName: userToEdit.firstName?.trim() ?? "",
      LastName: userToEdit.lastName?.trim() ?? "",
      Email: userToEdit.email?.trim() ?? "",
      SelectedCustomerRoleIds: (userToEdit.availableCustomerRoles ?? [])
        .filter((r) => r.selected)
        .map((r) => Number(r.value))
        .filter((id) => !Number.isNaN(id)),
      SelectedShipToes: shipTos,
    };

    try {
      setLoading(true);
      setError(null);

      const res = await dataService.updateSecondaryUser(payload, userToEdit.id);

      if (res?.success && res.data === true) {
        setEditSuccessDialogOpen(true);
        closeEditDialog();
      } else {
        setError(
          res?.error ||
            "Failed to update user. Please check the details and try again."
        );
      }
    } catch (e: any) {
      setError(
        e?.response?.data?.message ||
          e?.message ||
          "An error occurred while updating the user."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNewUser = async (shipTos: ShipToSettingModel[]) => {
    if (!userToEdit) return;

    if (!validateUser(userToEdit)) return;

    const payload: SecondaryUserRequest = {
      FirstName: userToEdit.firstName?.trim() ?? "",
      LastName: userToEdit.lastName?.trim() ?? "",
      Email: userToEdit.email?.trim() ?? "",
      SelectedCustomerRoleIds: (userToEdit.availableCustomerRoles ?? [])
        .filter((r) => r.selected)
        .map((r) => Number(r.value))
        .filter((id) => !Number.isNaN(id)),
      SelectedShipToes: shipTos,
    };

    try {
      setLoading(true);
      setError(null);

      const res = await dataService.addSecondaryUser(payload);

      if (res?.success && res.data === true) {
        setCreateSuccessDialogOpen(true);
        closeAddDialog();
      } else {
        setError(
          res?.error ||
            "Failed to create user. Please check the details and try again."
        );
      }
    } catch (e: any) {
      setError(
        e?.response?.data?.message ||
          e?.message ||
          "An error occurred while creating the user."
      );
    } finally {
      setLoading(false);
    }
  };
  const toggleRole = (roleValue: string, checked: boolean) => {
    setUserToEdit((old) => {
      if (!old) return old;

      const existingRoles =
        old.availableCustomerRoles && old.availableCustomerRoles.length > 0
          ? old.availableCustomerRoles
          : (model?.availableRoles ?? []).map((r) => ({
              ...r,
              selected: false,
            }));

      const updatedRoles = existingRoles.map((r) =>
        r.value === roleValue ? { ...r, selected: checked } : r
      );

      return {
        ...old,
        availableCustomerRoles: updatedRoles,
      };
    });
  };

  const handleConfirmDelete = async () => {
    try {
      setLoading(true);
      setError(null);

      await dataService.removeSecondaryUser(userToDelete?.id ?? 0);
    } catch (e: any) {
      setError(
        e?.response?.data?.message || e?.message || "Failed to approve user."
      );
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      loadManageUsers();
    }
  };

  const handleToggleBlockUser = async (user: CustomerUserDto) => {
    try {
      setLoading(true);
      setError(null);

      const res = await dataService.secondaryUserBlocked(user.id);

      if (res?.success) {
        await loadManageUsers();
      } else {
        setError(res?.error || "Failed to update user block status.");
      }
    } catch (e: any) {
      setError(
        e?.response?.data?.message ||
          e?.message ||
          "Block / unblock request failed."
      );
    } finally {
      setLoading(false);
    }
  };

  const startCreateNewUser = () => {
    setValidationErrors({});

    const newUser: CustomerUserDto = {
      id: 0,
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      isBlocked: false,
      lastLoginDate: "",
      availableCustomerRoles: [],
    };

    setUserToEdit(newUser);
    setAddDialogOpen(true);
    validateUser(newUser);
  };

  const loadManageUsers = useCallback(async () => {
    setPageLoading(true);
    setError(null);
    try {
      const res = await dataService.getManageUsers();
      if (res?.success && res.data) {
        if (res.data.isAccessDenied) window.location.href = "/lbpcustomer";
        setModel(res.data as ManageUsersResponse);
      }
    } catch (e: any) {
      setError(
        e?.response?.data?.message ||
          e?.message ||
          "Failed to load preferences."
      );
    } finally {
      setPageLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await loadManageUsers();
      if (cancelled) return;
    })();
    return () => {
      cancelled = true;
    };
  }, [loadManageUsers]);

  useEffect(() => {
    fetchAllCustomerShipTos();
  }, []);

  const fetchAllCustomerShipTos = async () => {
    try {
      setShipTosLoading(true);
      const response = await dataService.getAllCustomerShipTos(customerNumber);
      const shipTos: Lookup[] = response.data ?? [];
      if (shipTos.length > 0) {
        const grouped: OptionGroup[] = [
          {
            label: "Addresses",
            options: shipTos.map((addr: Lookup) => ({
              id: addr.value,
              name: addr.displayName,
            })),
          },
        ];

        setCustomerShipTos(grouped);
      }
    } catch (error) {
      console.error("Failed to load shiptos:", error);
    } finally {
      setShipTosLoading(false);
    }
  };

  if (pageLoading) return <PageLoading />;

  const renderName = (u: CustomerUserDto) => (
    <>
      {`${u.firstName ?? ""} ${u.lastName ?? ""}`.trim()}
      {u.isBlocked && (
        <Typography
          component="span"
          sx={{ display: "block", fontStyle: "italic", color: "#AF272F" }}
        >
          (Blocked)
        </Typography>
      )}
    </>
  );

  const isSaveDisabled =
    !userToEdit ||
    !userToEdit.firstName?.trim() ||
    !userToEdit.lastName?.trim() ||
    !userToEdit.email?.trim();

  function EditRow({ user }: { user: CustomerUserDto }) {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleMenuOpen = (e: React.MouseEvent<HTMLElement>) =>
      setAnchorEl(e.currentTarget);
    const handleMenuClose = () => setAnchorEl(null);

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
        }}
      >
        <Button
          className="whiteButtonOutline"
          sx={{
            textTransform: "uppercase",
            fontSize: { sm: "12px", md: "14px" },
          }}
          onClick={() => openEditDialog(user)}
        >
          Edit
        </Button>

        {isMobile && (
          <>
            <IconButton size="small" onClick={handleMenuOpen}>
              <MoreVertIcon sx={{ color: "#000" }} />
            </IconButton>

            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleMenuClose}
              slotProps={{
                paper: {
                  sx: {
                    borderRadius: "12px",
                    boxShadow: "0 6px 18px rgba(0,0,0,0.18)",
                    overflow: "hidden",
                  },
                },
              }}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <MenuItem
                disableRipple
                onClick={() => {
                  handleOpenDeleteDialog(user);
                  handleMenuClose();
                }}
                sx={{
                  minHeight: 0,
                  height: "22px",
                  lineHeight: "11px",
                  color: "#62B5E5",
                  fontSize: "12px",
                  fontFamily: '"Montserrat", Verdana, sans-serif',
                  "&:hover": { background: "transparent" },
                }}
              >
                Delete
              </MenuItem>

              <MenuItem
                disableRipple
                onClick={() => {
                  handleToggleBlockUser(user);
                  handleMenuClose();
                }}
                sx={{
                  minHeight: "0px",
                  height: "22px",
                  lineHeight: "11px",
                  color: "#AF272F",
                  fontSize: "12px",
                  fontFamily: '"Montserrat", Verdana, sans-serif',
                  "&:hover": { background: "transparent" },
                }}
              >
                {user.isBlocked ? "Un-Block" : "Block"}
              </MenuItem>
            </Menu>
          </>
        )}
      </Box>
    );
  }

  return (
    <>
      {loading ? (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            bgcolor: "transparent",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10000,
            pointerEvents: "auto",
          }}
        >
          <CircularProgress size={40} sx={{ color: "#1976d2" }} />
        </Box>
      ) : (
        <></>
      )}
      <Box sx={{ background: "#f9fafb", pb: 2, minHeight: "100vh" }}>
        <Box
          sx={{
            backgroundColor: "#62b5e5",
            marginBottom: "50px",
            minHeight: "100px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              color: "#fff",
              fontFamily: "'Montserrat', sans-serif",
              fontWeight: 600,
              fontSize: "36px",
            }}
          >
            Manage Users
          </Typography>
        </Box>

        <Paper sx={{ p: { xs: 2, md: 3 }, boxShadow: "none", border: "none" }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            rowGap={2}
            sx={{ mb: 2 }}
          >
            <Typography
              sx={{
                fontSize: "24px",
                fontFamily: '"droid_serifregular", Times New Roman, serif',
                color: "#494440",
                fontWeight: "normal",
              }}
            >
              Primary User: {model?.userName}
            </Typography>

            <Button
              className="whiteButtonOutline"
              sx={{
                textTransform: "uppercase",
                fontSize: "16px",
                padding: "0 35px",
                height: "50px",
              }}
              onClick={startCreateNewUser}
            >
              Add New User
            </Button>
          </Stack>

          <>
            {pendingUsers.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography
                  sx={{
                    fontSize: "24px",
                    fontFamily: '"droid_serifregular", Times New Roman, serif',
                    color: "#494440",
                    fontWeight: "normal",
                  }}
                  mb={1}
                >
                  Pending User(s)
                </Typography>
                <Table className="pendingUsersTable">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#62b5e5" }}>
                      <TableCell
                        className="usersTableCell"
                        sx={{ width: 40 }}
                      />
                      <TableCell className="usersTableCell">Name</TableCell>
                      <TableCell className="usersTableCell">Email</TableCell>
                      <TableCell className="usersTableCell">Phone</TableCell>
                      <TableCell className="usersTableCell" />
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {model?.pendingSecondaryUsers.map((u) => (
                      <React.Fragment key={u.id}>
                        <TableRow>
                          <TableCell sx={{ width: 40 }}></TableCell>

                          <TableCell
                            sx={{
                              textAlign: "center",
                              wordBreak: "break-word",
                            }}
                          >
                            {`${u.firstName} ${u.lastName}`}
                          </TableCell>

                          <TableCell
                            sx={{
                              textAlign: "center",
                              wordBreak: "break-all",
                            }}
                          >
                            {u.email}
                          </TableCell>

                          <TableCell
                            sx={{
                              textAlign: "center",
                              wordBreak: "break-word",
                            }}
                          >
                            {u.phone}
                          </TableCell>

                          <TableCell rowSpan={2}>
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: {
                                  xs: "column",
                                  sm: "column",
                                  md: "row",
                                },
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <Button
                                className="midGreenButtonOutline"
                                sx={{
                                  fontSize: {
                                    xs: "10px",
                                    sm: "12px",
                                    md: "14px",
                                  },
                                }}
                                onClick={() => handleApprovePendingUser(u)}
                              >
                                APPROVE
                              </Button>
                              <Button
                                className="midOrangeButtonOutline"
                                sx={{
                                  fontSize: {
                                    xs: "10px",
                                    sm: "12px",
                                    md: "14px",
                                  },
                                }}
                                onClick={() => handleDenyPendingUser(u)}
                              >
                                DENY
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>

                        <TableRow
                          sx={{
                            "& td": {
                              borderBottom:
                                "4px solid rgba(221, 221, 221, 0.867)",
                            },
                            "& td:not(:last-of-type)": {
                              borderRight: "1px solid #e0e0e0",
                            },
                          }}
                        >
                          <TableCell sx={{ width: 40 }} />

                          <TableCell colSpan={3}>
                            <Box
                              sx={{
                                p: 0.5,
                                display: "flex",
                                justifyContent: {
                                  xs: "center",
                                  sm: "flex-start",
                                },
                              }}
                            >
                              <FormGroup
                                sx={{
                                  display: "flex",
                                  flexDirection: "row",
                                  flexWrap: "wrap",
                                  gap: { xs: 1, sm: 2 },
                                }}
                              >
                                {model.availableRoles.map((role) => {
                                  const isChecked = (
                                    pendingUserRoles[u.id] ?? []
                                  ).includes(Number(role.value));

                                  return (
                                    <FormControlLabel
                                      key={role.value}
                                      control={
                                        <Checkbox
                                          checked={isChecked}
                                          onChange={() =>
                                            togglePendingRole(
                                              u.id,
                                              Number(role.value)
                                            )
                                          }
                                          size="small"
                                          sx={{
                                            padding: { xs: "2px", sm: "4px" },
                                            "& .MuiSvgIcon-root": {
                                              fontSize: {
                                                xs: "18px",
                                                sm: "20px",
                                              },
                                            },
                                          }}
                                        />
                                      }
                                      label={role.text}
                                      sx={{
                                        whiteSpace: "nowrap",
                                        "& .MuiFormControlLabel-label": {
                                          fontSize: { xs: "12px", sm: "14px" },
                                          fontFamily:
                                            '"proxima_nova_rgregular", "Helvetica Neue", Arial, sans-serif',
                                          color: "#494440",
                                        },
                                      }}
                                    />
                                  );
                                })}
                              </FormGroup>
                            </Box>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}

            <Table className="usersTable">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#62b5e5" }}>
                  {!isMobile && (
                    <TableCell
                      className="usersTableCell"
                      onClick={() => handleSort("firstName")}
                    >
                      Name{" "}
                      {sortField === "firstName"
                        ? sortDirection === "asc"
                          ? "▲"
                          : "▼"
                        : ""}
                    </TableCell>
                  )}
                  <TableCell
                    className="usersTableCell"
                    onClick={() => handleSort("email")}
                  >
                    Email{" "}
                    {sortField === "email"
                      ? sortDirection === "asc"
                        ? "▲"
                        : "▼"
                      : ""}
                  </TableCell>
                  {!isMobile && (
                    <TableCell
                      className="usersTableCell"
                      onClick={() => handleSort("lastLoginDate")}
                    >
                      Last Login Date{" "}
                      {sortField === "lastLoginDate"
                        ? sortDirection === "asc"
                          ? "▲"
                          : "▼"
                        : ""}
                    </TableCell>
                  )}
                  <TableCell className="usersTableCell"></TableCell>
                  {!isMobile && (
                    <TableCell className="usersTableCell"></TableCell>
                  )}
                </TableRow>
              </TableHead>

              <TableBody>
                {sortedSecondaryUsers.map((u) => (
                  <TableRow key={u.id} hover>
                    {!isMobile && <TableCell>{renderName(u)}</TableCell>}
                    <TableCell>{u.email}</TableCell>
                    {!isMobile && <TableCell>{u.lastLoginDate}</TableCell>}
                    <TableCell>
                      <EditRow user={u} />
                    </TableCell>
                    {!isMobile && (
                      <TableCell>
                        <Button
                          className="grayButtonOutline"
                          sx={{
                            textTransform: "uppercase",
                            fontSize: {
                              sm: "12px",
                              md: "14px",
                            },
                          }}
                          onClick={() => handleOpenDeleteDialog(u)}
                        >
                          Delete
                        </Button>

                        <Button
                          className={
                            u.isBlocked
                              ? "midGreenButtonOutline"
                              : "redButtonOutline"
                          }
                          sx={{
                            textTransform: "uppercase",
                            fontSize: {
                              sm: "12px",
                              md: "14px",
                            },
                          }}
                          onClick={() => handleToggleBlockUser(u)}
                        >
                          {u.isBlocked ? "UN-BLOCK" : "BLOCK"}
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {model?.secondaryUsers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography align="center">No users found.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </>
        </Paper>
      </Box>

      <SecondaryUserDialog
        mode="add"
        customerShipTos={customerShipTos}
        customerShipTosLoading={shipTosLoading}
        open={addDialogOpen}
        onClose={closeAddDialog}
        user={userToEdit}
        validationErrors={validationErrors}
        updateUserField={updateUserField}
        availableRoles={model?.availableRoles ?? []}
        toggleRole={toggleRole}
        isOrderNotificationSelected={false}
        onSave={handleSaveNewUser}
        isSaveDisabled={isSaveDisabled}
      />

      <SecondaryUserDialog
        mode="edit"
        customerShipTos={customerShipTos}
        customerShipTosLoading={shipTosLoading}
        open={editDialogOpen}
        onClose={closeEditDialog}
        user={userToEdit}
        validationErrors={validationErrors}
        updateUserField={updateUserField}
        availableRoles={model?.availableRoles ?? []}
        toggleRole={toggleRole}
        isOrderNotificationSelected={isOrderNotificationSelected}
        onSave={handleSaveEditedUser}
        isSaveDisabled={isSaveDisabled}
      />

      <AppDialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        content={
          <>
            Are you sure you want to delete {userToDelete?.firstName}{" "}
            {userToDelete?.lastName}
            <br />({userToDelete?.email})?
          </>
        }
        secondaryButton={{
          text: "CONTINUE",
          className: "blueButtonOutline",
          onClick: handleConfirmDelete,
        }}
        primaryButton={{
          text: "CLOSE",
          className: "blueButtonOutline",
          onClick: handleCloseDeleteDialog,
        }}
      />
      <AppDialog
        open={editSuccessDialogOpen}
        onClose={async () => {
          setEditSuccessDialogOpen(false);
          setEditedUser(null);
          await loadManageUsers();
        }}
        width={600}
        content={
          <>
            Save successful. Please notify the user {userToEdit?.firstName}{" "}
            {userToEdit?.lastName} ({userToEdit?.email}) of the updates to their
            account.
          </>
        }
        primaryButton={{
          text: "CLOSE",
          className: "blueButtonOutline",
          onClick: async () => {
            setEditSuccessDialogOpen(false);
            setEditedUser(null);
            await loadManageUsers();
          },
        }}
      />
      <AppDialog
        open={createSuccessDialogOpen}
        onClose={async () => {
          setCreateSuccessDialogOpen(false);
          setUserToEdit(null);

          await loadManageUsers();
        }}
        content={
          <>
            User successfully created. LansingNOW welcome email has been sent to
            this user’s email.
          </>
        }
        primaryButton={{
          text: "CLOSE",
          className: "blueButtonOutline",
          onClick: async () => {
            setCreateSuccessDialogOpen(false);
            await loadManageUsers();
          },
        }}
      />

      <AppDialog
        open={noRoleDialogOpen}
        onClose={() => setNoRoleDialogOpen(false)}
        content={<>Please select at least one user role to approve the user.</>}
        primaryButton={{
          text: "CLOSE",
          onClick: () => setNoRoleDialogOpen(false),
          className: "blueButtonOutline",
        }}
      />

      <AppDialog
        open={approveSuccessDialogOpen}
        onClose={async () => {
          setApproveSuccessDialogOpen(false);
          await loadManageUsers();
        }}
        content={
          <>
            User successfully approved. LansingNOW approve email has been sent
            to this user’s email.
          </>
        }
        primaryButton={{
          text: "CLOSE",
          className: "blueButtonOutline",
          onClick: async () => {
            setApproveSuccessDialogOpen(false);
            await loadManageUsers();
          },
        }}
      />

      <AppDialog
        open={denySuccessDialogOpen}
        onClose={() => {
          setDenySuccessDialogOpen(false);
          setDeniedUser(null);
        }}
        width={550}
        content={
          <>
            User successfully denied. LansingNOW denied email has been sent to
            this user’s email.
            <br />
            <br />
            If you want to undo{" "}
            <button
              type="button"
              style={{
                border: "none",
                background: "none",
                padding: 0,
                margin: 0,
                fontSize: "17px",
                fontWeight: 600,
                fontFamily: '"Montserrat", Verdana, sans-serif',
                color: "#62b5e5",
                cursor: "pointer",
              }}
              onClick={async () => {
                if (!deniedUser) return;
                const payload: ApproveUserRequest = {
                  approve: null,
                  customerRoleIds: [],
                };
                try {
                  setLoading(true);
                  setError(null);
                  const res = await dataService.approvePendingUser(
                    payload,
                    deniedUser.id
                  );

                  if (res.success) {
                    await loadManageUsers();
                    setDenySuccessDialogOpen(false);
                    setDeniedUser(null);
                  } else {
                    setError(res?.error || "Failed to undo deny.");
                  }
                } catch (e: any) {
                  setError(e?.response?.data?.message || e?.message);
                } finally {
                  setLoading(false);
                }
              }}
            >
              click here
            </button>
          </>
        }
        primaryButton={{
          text: "CLOSE",
          className: "blueButtonOutline",
          onClick: async () => {
            setDenySuccessDialogOpen(false);
            setDeniedUser(null);
            await loadManageUsers();
          },
        }}
      />
    </>
  );
};
